import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useAuthSession } from '@dukanti/shared';
import type { StoreMemberRole } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';

interface StoreMemberRoleRow {
  role: StoreMemberRole;
}

export interface CourierPermissions {
  readonly canManageCouriers: boolean;
  readonly role: StoreMemberRole | null;
}

export function useCourierPermissions(storeId: string | null): UseQueryResult<CourierPermissions> {
  const authSession = useAuthSession(supabase);
  const userId = authSession.user?.id ?? null;

  return useQuery<CourierPermissions>({
    queryKey: ['couriers', 'permissions', storeId, userId] as const,
    enabled: !!storeId && !!userId && !authSession.isLoading,
    staleTime: 30_000,
    queryFn: async () => {
      if (!storeId || !userId) {
        throw new Error('unreachable: enabled guard should prevent missing store/user');
      }

      const { data, error } = await supabase
        .from('store_members')
        .select('role')
        .eq('store_id', storeId)
        .eq('user_id', userId)
        .is('deleted_at', null)
        .maybeSingle<StoreMemberRoleRow>();

      if (error) throw error;

      const role = data?.role ?? null;
      return {
        role,
        canManageCouriers: role === 'owner' || role === 'admin',
      };
    },
  });
}
